import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import aseyori1 from '../assets/aseyori/1.jpeg';
import aseyori2 from '../assets/aseyori/2.jpeg';
import aseyori3 from '../assets/aseyori/3.jpeg';
import aseyori4 from '../assets/aseyori/4.jpeg';
import aseyori5 from '../assets/aseyori/5.jpeg';
import aseyori6 from '../assets/aseyori/6.jpeg';
import aseyori7 from '../assets/aseyori/7.jpeg';

import ngotb1 from '../assets/ngotb/1.jpeg';
import ngotb2 from '../assets/ngotb/2.jpeg';

import tete1 from '../assets/tete/1.jpeg';

import youthaware1 from '../assets/youthaware/1.jpeg';
import youthaware2 from '../assets/youthaware/2.jpeg';
import youthaware3 from '../assets/youthaware/3.jpeg';
import youthaware4 from '../assets/youthaware/4.jpeg';
import youthaware5 from '../assets/youthaware/5.jpeg';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const expandWidth = keyframes`
  from { width: 0; }
  to { width: 80px; }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
  animation: ${fadeIn} 0.8s ease;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: #2e7d32;
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
  display: inline-block;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #4CAF50, #ff9800);
    border-radius: 2px;
    animation: ${expandWidth} 1s ease;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
`;

const EventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const EventCard = styled.article`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.08);
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease forwards;
  opacity: 0;
  display: flex;
  flex-direction: column;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(to right, #4CAF50, #2e7d32);
    transform: scaleX(0);
    transition: transform 0.4s ease;
    z-index: 2;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  &:hover {
    transform: translateY(-15px) scale(1.02);
    box-shadow: 0 20px 50px rgba(46, 125, 50, 0.2);
  }
`;

const CarouselWrap = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 15px 15px 0 0;
  overflow: hidden;
`;

const Track = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slide = styled.div<{ $src: string }>`
  flex: 0 0 100%;
  height: 100%;
  background-image: url(${p => p.$src});
  background-size: cover;
  background-position: center;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.35));
  }
`;

const Counter = styled.span`
  position: absolute;
  top: 10px;
  right: 12px;
  background: rgba(0,0,0,0.5);
  color: #fff;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  pointer-events: none;
  z-index: 2;
`;

const Dots = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10px;
  display: flex;
  justify-content: center;
  gap: 6px;
  z-index: 2;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  border: 0;
  padding: 0;
  background: ${p => p.$active ? '#ffffff' : 'rgba(255,255,255,0.55)'};
  box-shadow: 0 0 4px rgba(0,0,0,0.4);
  cursor: pointer;
  transition: background 200ms ease, transform 200ms ease;

  &:hover {
    transform: scale(1.25);
  }
`;

const SoloPhoto = styled.div<{ $src: string }>`
  width: 100%;
  height: 200px;
  background-image: url(${p => p.$src});
  background-size: cover;
  background-position: center;
  border-radius: 15px 15px 0 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 60%, rgba(46,125,50,0.18));
    border-radius: 15px 15px 0 0;
  }
`;

const EventBody = styled.div`
  padding: 1.75rem 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
`;

const Eyebrow = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #4CAF50;
`;

const EventTitle = styled.h3`
  color: #2e7d32;
  font-size: 1.25rem;
  line-height: 1.4;
  margin: 0;
`;

const Meta = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0;
`;

const Prose = styled.p`
  font-size: 0.95rem;
  color: #444;
  line-height: 1.6;
  margin: 0;
`;

type Event = {
  tag: string;
  title: string;
  location: string;
  prose: string;
  photos: string[];
};

const EVENTS: Event[] = [
  {
    tag: 'Community Mobilisation',
    title: 'SRA joins Aseyori to mobilise for TB tests at a community programme in Alimosho.',
    location: '📍 Alimosho, Lagos',
    prose:
      'Door-to-door outreach and an on-site screening station — the room filled by mid-morning and stayed full.',
    photos: [aseyori1, aseyori2, aseyori3, aseyori4, aseyori5, aseyori6, aseyori7],
  },
  {
    tag: 'World TB Day',
    title: 'SRA joins other NGOs to celebrate TB Day.',
    location: '📍 NGO Coalition',
    prose:
      'A day of speeches alongside partner NGOs — and the paperwork that signed on more wards for the year ahead.',
    photos: [ngotb1, ngotb2],
  },
  {
    tag: 'Stakeholder Engagement',
    title: 'SRA holds a tête-à-tête with TBLS in Lagos West.',
    location: '📍 Lagos West',
    prose:
      'A small-room conversation with TBLS leadership — quietly shifting which wards see the screening van next quarter.',
    photos: [tete1],
  },
  {
    tag: 'Awareness Programme',
    title: 'SRA creates awareness to youth leaders on TB / HIV.',
    location: '📍 Youth Leadership',
    prose:
      'Training youth-leader peer educators on TB and HIV — equipping them to carry the conversation home.',
    photos: [youthaware1, youthaware2, youthaware3, youthaware4, youthaware5],
  },
];

type CarouselProps = {
  photos: string[];
  alt: string;
};

const PhotoCarousel: React.FC<CarouselProps> = ({ photos, alt }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  if (photos.length === 1) {
    return <SoloPhoto $src={photos[0]} role="img" aria-label={alt} />;
  }

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const newIndex = Math.round(el.scrollLeft / el.clientWidth);
    if (newIndex !== index) setIndex(newIndex);
  };

  const go = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <CarouselWrap>
      <Track ref={trackRef} onScroll={onScroll}>
        {photos.map((src, i) => (
          <Slide
            key={i}
            $src={src}
            role="img"
            aria-label={`${alt} — photo ${i + 1} of ${photos.length}`}
          />
        ))}
      </Track>
      <Counter>{index + 1} / {photos.length}</Counter>
      <Dots>
        {photos.map((_, i) => (
          <Dot
            key={i}
            $active={i === index}
            onClick={() => go(i)}
            aria-label={`Go to photo ${i + 1}`}
          />
        ))}
      </Dots>
    </CarouselWrap>
  );
};

const Events: React.FC = () => {
  return (
    <Section id="impact">
      <Title>Field Activities</Title>
      <Subtitle>
        A working record of where the team has shown up — communities, partners,
        conversations.
      </Subtitle>

      <EventGrid>
        {EVENTS.map((ev, i) => (
          <EventCard key={i}>
            <PhotoCarousel photos={ev.photos} alt={ev.title} />
            <EventBody>
              <Eyebrow>{ev.tag}</Eyebrow>
              <EventTitle>{ev.title}</EventTitle>
              <Meta>{ev.location}</Meta>
              <Prose>{ev.prose}</Prose>
            </EventBody>
          </EventCard>
        ))}
      </EventGrid>
    </Section>
  );
};

export default Events;
